import { Alert, Form, Modal } from 'antd';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useVForm, type UseVFormOptions, type VFormApi } from './useVForm';

/**
 * The default surface for create/edit modals. `useVForm` + `<Modal>` +
 * `<Form>` + Cancel/Submit + server-error `<Alert>` all in one. See
 * .claude/FORMS.md § What `<FormModal>` is.
 *
 * On close, the form and mutation reset — no stale error flashes on reopen.
 * `children` may be a render function receiving the `useVForm` api for the
 * rare cases that need `form.setFieldValue` or `Form.useWatch` on siblings.
 */
interface FormModalProps<TValues extends object, TResult>
  extends UseVFormOptions<TValues, TResult> {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  submitText?: string;
  cancelText?: string;
  width?: number;
  children: ReactNode | ((vform: VFormApi<TValues, TResult>) => ReactNode);
}

export function FormModal<TValues extends object, TResult = unknown>({
  open,
  onClose,
  title,
  submitText = 'Save',
  cancelText = 'Cancel',
  width = 560,
  children,
  ...vformOptions
}: FormModalProps<TValues, TResult>) {
  const vform = useVForm<TValues, TResult>(vformOptions);

  useEffect(() => {
    if (!open) vform.reset();
    // vform.reset is stable via useCallback; we only care about `open`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const body = typeof children === 'function' ? children(vform) : children;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={title}
      okText={submitText}
      cancelText={cancelText}
      confirmLoading={vform.isSubmitting}
      onOk={vform.submit}
      width={width}
      destroyOnHidden
    >
      <Form {...vform.formProps}>
        {body}
        {vform.serverError && (
          <Alert
            type="error"
            showIcon
            message={vform.serverError}
            style={{ marginTop: 8 }}
          />
        )}
      </Form>
    </Modal>
  );
}
